/**
 * The chosen week: four average tiles and the day table — kcal, protein, carbohydrate with its sugar
 * under it, fat, status — every column left aligned (the SurveyCompare pattern). A day's row opens
 * the client's meals and the day total, read from `/day` only when it is opened. Read-only.
 */

import { Fragment, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useNutritionDayEntries } from '../lib/useNutrition';
import { NUTRIENTS, unitOf, wholeNumber, type NutritionAmounts } from '../model/nutrition';
import { dayRowViews, toggleMeal, type MealItemView } from '../model/nutritionMeals';
import type { NutritionDayRow, NutritionWeekPane } from '../model/nutritionWeeks';

import { MacroGlyph, StatusGlyph } from './NutritionGlyphs';
import styles from './NutritionTab.module.css';

const KEY = 'web:clientDetail.nutrition';

interface NutritionWeekTableProps {
  readonly clientId: number;
  readonly pane: NutritionWeekPane;
  readonly openDay: string | null;
  readonly onToggleDay: (date: string) => void;
}

export function NutritionWeekTable({ clientId, pane, openDay, onToggleDay }: NutritionWeekTableProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className={styles.paneHead}>
        <div className={styles.paneHeadText}>
          <h2 className={styles.paneTitle}>{pane.title}</h2>
          <p className={styles.paneCaption}>{pane.caption}</p>
        </div>
      </div>
      <div className={styles.tiles}>
        {pane.tiles.map((tile) => (
          <div className={styles.tile} key={tile.nutrient}>
            <small className={styles.tileLabel}>
              <MacroGlyph nutrient={tile.nutrient} small />
              {t(`${KEY}.nutrientShort.${tile.nutrient}`)}
            </small>
            <b className={styles.tileValue}>
              {tile.value ?? '—'}
              {tile.value === null ? null : <em className={styles.tileUnit}>{tile.unit}</em>}
            </b>
            <span className={styles.tileNote}>{tile.note}</span>
          </div>
        ))}
      </div>
      {/* A scroll container needs a way in from the keyboard: the ARIA practices' named region. */}
      <div aria-label={pane.title} className={styles.tableScroll} role="region" tabIndex={0}>
        <table className={styles.weekTable}>
          <colgroup>
            <col className={styles.colDay} />
            <col className={styles.colEnergy} />
            <col className={styles.colMacro} />
            <col className={styles.colCarbs} />
            <col className={styles.colMacro} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th className={styles.th} scope="col">
                {t(`${KEY}.colDay`)}
              </th>
              {NUTRIENTS.map((nutrient) => (
                <th className={styles.th} key={nutrient} scope="col">
                  <MacroGlyph nutrient={nutrient} small />
                  {t(`${KEY}.nutrient.${nutrient}`)}
                </th>
              ))}
              <th className={styles.th} scope="col">
                {t(`${KEY}.colStatus`)}
              </th>
            </tr>
          </thead>
          {pane.rows.map((row) => (
            <DayRows
              clientId={clientId}
              key={row.date}
              onToggle={onToggleDay}
              open={openDay === row.date}
              row={row}
            />
          ))}
        </table>
      </div>
    </>
  );
}

function DayRows({
  clientId,
  row,
  open,
  onToggle,
}: {
  readonly clientId: number;
  readonly row: NutritionDayRow;
  readonly open: boolean;
  readonly onToggle: (date: string) => void;
}) {
  const { t } = useTranslation();
  const entriesId = useId();
  const dayCell = (
    <th className={styles.dayCell} scope="row">
      {row.logged ? (
        <button
          aria-controls={open ? entriesId : undefined}
          aria-expanded={open}
          aria-label={t(`${KEY}.toggleDay`, { day: `${row.dayName}, ${row.dateLabel}` })}
          className={styles.dayToggle}
          onClick={(event) => {
            event.stopPropagation();
            onToggle(row.date);
          }}
          type="button"
        >
          <Chevron open={open} />
        </button>
      ) : (
        <span className={styles.dayToggleGap} />
      )}
      <span className={styles.dayText}>
        <b className={styles.dayName}>
          {row.dayName}
          {row.today ? <em className={styles.todayTag}>{t(`${KEY}.today`)}</em> : null}
        </b>
        <span className={styles.dayDate}>
          {row.dateLabel}
          {open && row.meals !== null ? ` · ${row.meals}` : null}
        </span>
      </span>
    </th>
  );
  const status = (
    <td className={styles.td}>
      <span className={styles.status} title={row.status.all ?? undefined}>
        <StatusGlyph kind={row.status.kind} share={row.status.share} />
        <span
          className={
            row.status.kind === 'off'
              ? styles.statusTextOff
              : row.status.kind === 'none' || row.status.kind === 'unjudged'
                ? styles.statusTextMuted
                : styles.statusText
          }
        >
          {row.status.text}
        </span>
        {row.status.more === null ? null : <span className={styles.statusMore}>{row.status.more}</span>}
      </span>
    </td>
  );

  if (!row.logged) {
    return (
      <tbody>
        <tr className={styles.rowNone}>
          {dayCell}
          <td className={`${String(styles.td)} ${String(styles.nothing)}`} colSpan={4}>
            {t(`${KEY}.nothingLogged`)}
          </td>
          {status}
        </tr>
      </tbody>
    );
  }

  return (
    <>
      <tbody>
        <tr
          className={styles.rowDay}
          onClick={() => {
            onToggle(row.date);
          }}
        >
          {dayCell}
          {row.cells.map((cell) => {
            const partial = cell.partial || (cell.nutrient === 'carbs' && row.sugarPartial);
            return (
              <td
                className={`${String(styles.td)} ${String(styles.num)}`}
                key={cell.nutrient}
                title={partial ? t(`${KEY}.partialHint`) : undefined}
              >
                {cell.value === null ? (
                  <span className={styles.dash}>—</span>
                ) : (
                  <>
                    <span className={cell.off ? styles.numOff : undefined}>{cell.value}</span>
                    <small className={styles.numUnit}>{cell.unit}</small>
                  </>
                )}
                {partial ? <span className={hidden.srOnly}>{t(`${KEY}.partialHint`)}</span> : null}
                {cell.nutrient === 'carbs' && row.sugar !== null ? (
                  <span className={styles.numSugar}>{t(`${KEY}.sugarIn`, { value: row.sugar })}</span>
                ) : null}
              </td>
            );
          })}
          {status}
        </tr>
      </tbody>
      {open ? <EntryRows clientId={clientId} date={row.date} id={entriesId} /> : null}
    </>
  );
}

function EntryRows({
  clientId,
  date,
  id,
}: {
  readonly clientId: number;
  readonly date: string;
  readonly id: string;
}) {
  const { t, i18n } = useTranslation();
  const day = useNutritionDayEntries(clientId, date);
  const [openMeals, setOpenMeals] = useState<ReadonlySet<string>>(() => new Set());
  const meals = day.day === null ? [] : dayRowViews(day.day, t, i18n.language);
  if (day.pending || day.failed || meals.length === 0) {
    return (
      <tbody id={id}>
        <tr className={`${String(styles.rowEntry)} ${String(styles.rowEntryLast)}`}>
          <td className={`${String(styles.td)} ${String(styles.entryNote)}`} colSpan={6} aria-busy={day.pending}>
            {day.pending
              ? t(`${KEY}.entriesLoading`)
              : day.failed
                ? t(`${KEY}.entriesFailed`)
                : t(`${KEY}.entriesEmpty`)}
          </td>
        </tr>
      </tbody>
    );
  }
  const lastRow = (index: number, followed: boolean) => index === meals.length - 1 && !followed;
  return (
    <tbody id={id}>
      {meals.map((meal, index) => {
        const mealOpen = meal.expandable && openMeals.has(meal.key);
        const itemId = (itemIndex: number) => `${id}-${meal.key}-${String(itemIndex)}`;
        const toggle = () => {
          setOpenMeals((open) => toggleMeal(open, meal.key));
        };
        return (
          <Fragment key={meal.key}>
            <tr
              className={`${String(styles.rowEntry)} ${meal.expandable ? String(styles.rowMeal) : ''} ${lastRow(index, mealOpen) ? String(styles.rowEntryLast) : ''}`}
              onClick={meal.expandable ? toggle : undefined}
            >
              <th className={`${String(styles.td)} ${String(styles.entryName)}`} scope="row" title={meal.title}>
                <span className={styles.entryLine}>
                  {meal.expandable ? (
                    <button
                      aria-controls={
                        mealOpen ? meal.items.map((_, itemIndex) => itemId(itemIndex)).join(' ') : undefined
                      }
                      aria-expanded={mealOpen}
                      aria-label={`${t(`${KEY}.${mealOpen ? 'hideItems' : 'showItems'}`)}: ${meal.title}`}
                      className={`${String(styles.dayToggle)} ${String(styles.mealToggle)}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggle();
                      }}
                      type="button"
                    >
                      <Chevron open={mealOpen} />
                    </button>
                  ) : null}
                  <span className={styles.entryText}>
                    <span className={styles.entryNameText}>
                      {meal.title}
                      {meal.quiet === null ? null : <span className={styles.entryQuiet}> · {meal.quiet}</span>}
                    </span>
                    {meal.count === null ? null : <span className={styles.entryMeta}>{meal.count}</span>}
                    {meal.single === null ? null : <ItemMeta item={meal.single} named />}
                  </span>
                </span>
              </th>
              <AmountCells amounts={meal.amounts} />
              <td className={styles.td} />
            </tr>
            {mealOpen
              ? meal.items.map((item, itemIndex) => (
                  <tr
                    className={`${String(styles.rowEntry)} ${String(styles.rowItem)} ${lastRow(index, itemIndex < meal.items.length - 1) ? String(styles.rowEntryLast) : ''}`}
                    id={itemId(itemIndex)}
                    key={item.key}
                  >
                    <th className={`${String(styles.td)} ${String(styles.itemName)}`} scope="row" title={item.name}>
                      <span className={styles.entryNameText}>{item.name}</span>
                      <ItemMeta item={item} />
                    </th>
                    <AmountCells amounts={item.amounts} />
                    <td className={styles.td} />
                  </tr>
                ))
              : null}
          </Fragment>
        );
      })}
    </tbody>
  );
}

/** Kcal, protein, carbohydrate with its sugar under it, fat — one meal's or one item's. */
function AmountCells({ amounts }: { readonly amounts: NutritionAmounts }) {
  const { t, i18n } = useTranslation();
  return NUTRIENTS.map((nutrient) => {
    const value = amounts[nutrient];
    return (
      <td className={`${String(styles.td)} ${String(styles.num)}`} key={nutrient}>
        {value === null ? (
          <span className={styles.dash}>—</span>
        ) : (
          <>
            {wholeNumber(value, i18n.language)}
            <small className={styles.numUnit}>{unitOf(nutrient)}</small>
          </>
        )}
        {nutrient === 'carbs' && amounts.sugar !== null ? (
          <span className={styles.numSugar}>
            {t(`${KEY}.sugarIn`, { value: wholeNumber(amounts.sugar, i18n.language) })}
          </span>
        ) : null}
      </td>
    );
  });
}

/**
 * Grams and brand of a scanned product, and its Open Food Facts attribution, which the ODbL asks for.
 * `named`: the item stands in its meal's row, so its own name leads.
 */
function ItemMeta({ item, named = false }: { readonly item: MealItemView; readonly named?: boolean }) {
  const { t } = useTranslation();
  const facts = [named ? item.name : null, item.grams, item.brand, item.note]
    .filter((part) => part !== null)
    .join(' · ');
  if (facts === '' && item.sourceUrl === null) return null;
  const source = t(`${KEY}.productSource`);
  return (
    <span className={styles.entryMeta}>
      {facts}
      {facts !== '' && item.sourceUrl !== null ? ' · ' : null}
      {item.sourceUrl === null ? null : (
        <a
          aria-label={t(`${KEY}.productSourceOpens`, { label: source })}
          className={styles.entrySource}
          href={item.sourceUrl}
          onClick={(event) => {
            event.stopPropagation();
          }}
          rel="noopener noreferrer"
          target="_blank"
        >
          {source}
        </a>
      )}
    </span>
  );
}

function Chevron({ open }: { readonly open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={`${String(styles.chevron)} ${open ? String(styles.chevronOpen) : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 16 16"
    >
      <path d="M6 4l4 4-4 4" />
    </svg>
  );
}
